// Custom orders for memories. An album keeps its order on each memory
// (`order`, higher first: the album reorder writes count - 1 - position, so a
// memory added later, at 0, lands last); a vault keeps its own list of memory
// ids, first to last (`memoryOrder`), so arranging "All memories" never
// reorders an album. The mobile app shares these rules.

type Ordered = { id: number; order?: number };

// An album has a custom order once any memory has been given a place.
export const hasAlbumOrder = (memories: Ordered[]) =>
  memories.some((memory) => (memory.order ?? 0) !== 0);

// An album's memories in its custom order; ties keep their given order.
export const byAlbumOrder = <T extends Ordered>(memories: T[]): T[] =>
  memories
    .map((memory, index) => ({ index, memory }))
    .sort(
      (a, b) =>
        (b.memory.order ?? 0) - (a.memory.order ?? 0) || a.index - b.index,
    )
    .map(({ memory }) => memory);

// What PUT /api/contents/order takes to put an album's memories in this order.
export const albumOrderRequest = (ids: number[]) =>
  ids.map((id, index) => ({
    contentId: id,
    order: (ids.length - 1 - index).toString(),
  }));

// Memories in a vault's custom order: those in the list first, in its order;
// any not in it yet (added since) after them, as given.
export const byIdOrder = <T extends { id: number }>(
  memories: T[],
  ids: number[],
): T[] => {
  const place = new Map(ids.map((id, index) => [id, index]));
  return memories
    .map((memory, index) => ({ index, memory, place: place.get(memory.id) }))
    .sort((a, b) => {
      if (a.place !== undefined && b.place !== undefined) {
        return a.place - b.place;
      }
      if (a.place !== undefined) return -1;
      if (b.place !== undefined) return 1;
      return a.index - b.index;
    })
    .map(({ memory }) => memory);
};

// The list after dragging the item at `from` to `to`.
export const moveItem = <T>(items: T[], from: number, to: number): T[] => {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};
